import { useUserStore } from "@/stores";
import { dashboardCache } from "@/utils/dashboard-cache";
import { useEffect, useRef } from "react";

/**
 * Hook to manage dashboard cache lifecycle
 * Handles cache invalidation when organization changes
 */
export const useDashboardCache = () => {
	const user = useUserStore((state) => state.user);
	const previousOrgId = useRef<number | null>(null);

	useEffect(() => {
		const currentOrgId = user?.organization?.id || user?.organization_id;

		// If organization changed, invalidate all caches
		if (previousOrgId.current && previousOrgId.current !== currentOrgId) {
			console.log(
				`Organization changed from ${previousOrgId.current} to ${currentOrgId}, clearing caches`,
			);
			dashboardCache.clear();
		}

		// Update the previous org ID
		previousOrgId.current = currentOrgId || null;
	}, [user?.organization?.id, user?.organization_id]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			// Don't clear cache on unmount, as it should persist across navigation
			// Only clear on organization change or explicit actions
		};
	}, []);

	return {
		/**
		 * Clear all cached data
		 */
		clearCache: dashboardCache.clear.bind(dashboardCache),

		/**
		 * Clear cache for current organization
		 */
		clearOrganizationCache: () => {
			const currentOrgId = user?.organization?.id || user?.organization_id;
			if (currentOrgId) {
				dashboardCache.invalidateOrganization(currentOrgId);
			}
		},

		/**
		 * Get cache statistics
		 */
		getCacheStats: dashboardCache.getStats.bind(dashboardCache),
	};
};
